import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Camera, Plane, Globe, Sparkles, Star, MapPin, Phone, Mail, Award, Users, Calendar, Zap } from "lucide-react";
import { useEffect, useState } from "react";

const Index = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const internationalReferences = [
    { name: "St. Moritz", country: "Schweiz", type: "Luxushotels" },
    { name: "Ischgl", country: "Österreich", type: "Skiresorts" },
    { name: "Arlberg", country: "Österreich", type: "Premium Hotels" },
    { name: "Mailand", country: "Italien", type: "Fashion & Design" },
    { name: "Verona", country: "Italien", type: "Kulturelle Stätten" },
    { name: "Sylt", country: "Deutschland", type: "Luxusresorts" },
    { name: "Wien", country: "Österreich", type: "Palais & Hotels" },
    { name: "Zürich", country: "Schweiz", type: "Business Hotels" }
  ];

  const services = [
    {
      icon: Camera,
      title: "Professionelle Fotografie",
      description: "36 Jahre Erfahrung in der Pressefotografie und Hotelfotografie mit HDR-Technik und situationsgemäßer Fotografie mit Models.",
      features: ["HDR-Technik", "Pressefotografie", "Hotelfotografie", "Work-Fotografie"]
    },
    {
      icon: Globe,
      title: "360° Virtuelle Touren",
      description: "Hochwertige virtuelle Touren auf HTML5-Basis für Web und Smartphone. Buchungen direkt in der Tour möglich.",
      features: ["HTML5-basiert", "Smartphone-optimiert", "Buchungsfunktion", "Social Media Integration"]
    },
    {
      icon: Plane,
      title: "Luftbildfotografie",
      description: "Modernste Drohnentechnik für atemberaubende Luftaufnahmen und 360° Panoramaaufnahmen aus der Luft.",
      features: ["Neueste Drohnentechnik", "360° Luftpanoramen", "Drohnenvideos", "Europaweit verfügbar"]
    },
    {
      icon: Sparkles,
      title: "KI-Kreativlösungen",
      description: "Innovative KI-gestützte Dienstleistungen: Bildrestaurierung, Animation historischer Fotos und kreative Marketinglösungen.",
      features: ["Bildrestaurierung", "Foto-Animation", "KI-Marketing", "Kreative Designs"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        />
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Hero Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1548552554-ac8ad1b37d6b?w=1920&auto=format&fit=crop&q=80')`,
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        />
        
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <Badge className="mb-10 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 px-6 py-2 text-lg font-semibold">
            38 Jahre Gesamterfahrung
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-amber-200 bg-clip-text text-transparent leading-relaxed">
            Luxuriöse Werbeagentur
          </h1>
          
          <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto leading-relaxed">
            Die Fusion von <span className="text-amber-300 font-semibold">pixnetmedia (36 Jahre)</span> und <span className="text-amber-300 font-semibold">KI Werbestudio (2 Jahre)</span> –
            Europaweit tätig mit internationalen Referenzen.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300" asChild>
              <a href="http://formular.pixnetmedia.de" target="_blank" rel="noopener noreferrer">
                pixnetmedia
              </a>
            </Button>
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300" asChild>
              <a href="https://www.ki-werbestudio.de/gewerbe" target="_blank" rel="noopener noreferrer">
                KI-Werbestudio
              </a>
            </Button>
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300" asChild>
              <a href="https://docs.google.com/presentation/d/e/2PACX-1vTZXu2viwCEOTejU7qBXORiG0wuOwt9WCdIyTSp9bAji8PNPLoL9ly1zi2qDdLVAA/pub?start=true&loop=true&delayms=10000" target="_blank" rel="noopener noreferrer">
                Infos zur Fusion
              </a>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-300 mb-2">1000+</div>
              <div className="text-blue-100">Zufriedene Kunden</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-300 mb-2">500+</div>
              <div className="text-blue-100">Projekte abgeschlossen</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-300 mb-2">38</div>
              <div className="text-blue-100">Jahre Gesamterfahrung</div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600 to-slate-700 text-white px-4 py-2">
              Unsere Dienstleistungen
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-blue-800 bg-clip-text text-transparent">
              Premium-Services für höchste Ansprüche
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Von traditioneller Fotografie bis zu innovativen KI-Lösungen – wir bieten das komplette Spektrum moderner Werbeagentur-Services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="group hover:shadow-2xl transition-all duration-500 border-0 bg-gradient-to-br from-white to-slate-50 hover:from-blue-50 hover:to-white">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-600 to-slate-700 rounded-xl text-white group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-6 w-6" />
                      </div>
                      <CardTitle className="text-2xl font-bold text-slate-800">{service.title}</CardTitle>
                    </div>
                    <CardDescription className="text-lg text-slate-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* International References */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 px-4 py-2">
              Internationale Referenzen
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              Europaweit erfolgreich
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Unsere Expertise erstreckt sich über die schönsten und exklusivsten Destinationen Europas.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {internationalReferences.map((ref, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300 group">
                <CardContent className="p-6 text-center">
                  <MapPin className="h-8 w-8 text-amber-400 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-xl font-bold text-white mb-2">{ref.name}</h3>
                  <p className="text-blue-200 mb-2">{ref.country}</p>
                  <Badge variant="outline" className="border-amber-400 text-amber-400">
                    {ref.type}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600 to-slate-700 text-white px-4 py-2">
              Unsere Geschichte
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-blue-800 bg-clip-text text-transparent">
              38 Jahre Expertise vereint
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Card className="p-8 bg-gradient-to-br from-blue-50 to-white border-l-4 border-l-blue-600">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-600 rounded-full text-white">
                    <Camera className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">pixnetmedia</h3>
                    <p className="text-blue-600 font-semibold">Seit 1989 • 35 Jahre Erfahrung</p>
                  </div>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Traditionelle Fotografie und 360° Touren. Pressefotograf für Sachsens größte Tageszeitung "Freie Presse". 
                  Spezialisiert auf Hotelfotografie, Luftbildaufnahmen und virtuelle Touren für die Tourismus- und Gastronomiebranche.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge className="bg-blue-100 text-blue-800">Pressefotografie</Badge>
                  <Badge className="bg-blue-100 text-blue-800">360° Touren</Badge>
                  <Badge className="bg-blue-100 text-blue-800">Luftbildfotografie</Badge>
                </div>
              </Card>
              
              <Card className="p-8 bg-gradient-to-br from-amber-50 to-white border-l-4 border-l-amber-500">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-amber-500 rounded-full text-white">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">KI Werbestudio</h3>
                    <p className="text-amber-600 font-semibold">Seit 2022 • 2 Jahre Innovation</p>
                  </div>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  Innovative KI-gestützte Kreativlösungen. Spezialisiert auf kreative Marketinglösungen sowie Videoproduktion durch modernste KI-Technologie.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge className="bg-amber-100 text-amber-800">KI-Bildbearbeitung</Badge>
                  <Badge className="bg-amber-100 text-amber-800">Foto-Animation</Badge>
                  <Badge className="bg-amber-100 text-amber-800">Kreative Designs</Badge>
                </div>
              </Card>
            </div>
            
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1495754149474-e54c07932677?w=600&auto=format&fit=crop&q=80" 
                  alt="Luxuriöse Hotellobby" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">38</div>
                <div className="text-sm font-semibold">Jahre vereinte Expertise</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 px-4 py-2">
              Kontakt
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              Lassen Sie uns Ihre Vision verwirklichen
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Bereit für Ihr nächstes Premium-Projekt? Kontaktieren Sie uns für eine persönliche Beratung.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-8">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl font-bold text-white mb-4">Kontaktinformationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500 rounded-full">
                    <Phone className="h-5 w-5 text-slate-900" />
                  </div>
                  <div>
                    <p className="text-amber-300 font-semibold">Telefon</p>
                    <p className="text-white">+49 (0)151 4312 0308</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500 rounded-full">
                    <Mail className="h-5 w-5 text-slate-900" />
                  </div>
                  <div>
                    <p className="text-amber-300 font-semibold">E-Mail</p>
                    <p className="text-white">info@pixnetmedia.de</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500 rounded-full">
                    <MapPin className="h-5 w-5 text-slate-900" />
                  </div>
                  <div>
                    <p className="text-amber-300 font-semibold">Adresse</p>
                    <p className="text-white">Hammerstraße 9<br />08459 Neukirchen, Deutschland</p>
                  </div>
                </div>
                
                <Separator className="bg-white/20" />
                
                <div className="text-center">
                  <p className="text-blue-200 mb-4">Geschäftsführer: Mario Dudacy</p>
                  <p className="text-blue-200">USt.-Nr. DE 114 103 514</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 p-8">
              <CardHeader className="pb-6">
                <CardTitle className="text-2xl font-bold text-white mb-4">Warum uns wählen?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <Star className="h-6 w-6 text-amber-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-2">Internationale Expertise</h4>
                    <p className="text-blue-200">Erfolgreiche Projekte in den exklusivsten Destinationen Europas</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Zap className="h-6 w-6 text-amber-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-2">Innovation trifft Tradition</h4>
                    <p className="text-blue-200">38 Jahre bewährte Erfahrung kombiniert mit modernster KI-Technologie</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Users className="h-6 w-6 text-amber-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-2">Persönlicher Service</h4>
                    <p className="text-blue-200">Individuelle Betreuung und maßgeschneiderte Lösungen für jeden Kunden</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Award className="h-6 w-6 text-amber-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-2">Bewährte Qualität</h4>
                    <p className="text-blue-200">Über 1000 zufriedene Kunden und 500+ erfolgreich abgeschlossene Projekte</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-12 py-4 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300">
              <Phone className="mr-2 h-5 w-5" />
              +49(0)151 4312 0308
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              Luxuriöse Werbeagentur
            </h3>
            <p className="text-slate-400 mb-6">
              Die Fusion von Pixnet Media und KI Werbestudio – 38 Jahre vereinte Expertise
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300" asChild>
                <a href="http://formular.pixnetmedia.de" target="_blank" rel="noopener noreferrer">
                  pixnetmedia
                </a>
              </Button>
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300" asChild>
                <a href="https://www.ki-werbestudio.de/gewerbe" target="_blank" rel="noopener noreferrer">
                  KI-Werbestudio
                </a>
              </Button>
            </div>
            
            <div className="flex justify-center space-x-8 text-sm text-slate-400">
              <span>© 2024 Alle Rechte vorbehalten</span>
              <span>•</span>
              <a href="http://formular.pixnetmedia.de/content/impressum/" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">Impressum</a>
              <span>•</span>
              <a href="http://formular.pixnetmedia.de/content/datenschutz/" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">Datenschutz</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
